// CouriersPageXL - Sprint 44 Fase 3 Vista 6.
// Contenedor: header con titulo + boton "Cargar presets" + welcome banner +
// msg banner + grid 2:1 (CouriersListXL + CourierFormXL).
import { useEffect, useState } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import useCouriersXL from './hooks/useCouriersXL';
import CouriersListXL from './CouriersListXL';
import CourierFormXL from './CourierFormXL';
import { IconSparkles } from './components/Icons';

const CSS_ID = 'xl-cou-page-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-page__head {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 16px; flex-wrap: wrap;
}
.xl-page__title { font-size: 22px; font-weight: 600; margin: 0 }
.xl-page__subtitle { font-size: 13px; color: var(--xl-color-text-secondary); margin: 2px 0 0 }

.xl-cou-presets {
  font: 500 13px/1 var(--xl-font-sans);
  padding: 10px 16px;
  border-radius: var(--xl-radius-md);
  border: 1px solid transparent;
  background: #6366F1;
  color: var(--xl-color-text-inverse);
  cursor: pointer;
  display: inline-flex; align-items: center; gap: 8px;
  transition: filter 120ms;
}
.xl-cou-presets:hover { filter: brightness(1.1) }
.xl-cou-presets:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-cou-presets:disabled { opacity: 0.45; cursor: not-allowed }
[data-xl-theme="dark"] .xl-cou-presets { background: #818CF8 }

.xl-cou-welcome {
  display: flex; gap: 12px; align-items: flex-start;
  padding: 12px 14px;
  margin-bottom: 16px;
  background: rgba(99,102,241,0.10);
  border: 1px solid rgba(99,102,241,0.32);
  border-radius: var(--xl-radius-md);
  font-size: 13px;
  color: var(--xl-color-text);
}
[data-xl-theme="dark"] .xl-cou-welcome {
  background: rgba(165,180,252,0.14);
  border-color: rgba(165,180,252,0.32);
}
.xl-cou-welcome__ico { color: #6366F1; margin-top: 2px; flex-shrink: 0 }
[data-xl-theme="dark"] .xl-cou-welcome__ico { color: #A5B4FC }
.xl-cou-welcome__title { font-weight: 600; color: var(--xl-color-text) }
.xl-cou-welcome__body {
  color: var(--xl-color-text-secondary);
  margin-top: 2px; line-height: 1.45;
}

.xl-banner {
  padding: 10px 14px; margin-bottom: 12px;
  border-radius: var(--xl-radius-md); font-size: 13px;
}
.xl-banner--ok  { background: rgba(5,150,105,0.10); color: var(--xl-color-success); border: 1px solid rgba(5,150,105,0.28) }
.xl-banner--err { background: rgba(220,38,38,0.10); color: var(--xl-color-danger);  border: 1px solid rgba(220,38,38,0.28) }

.xl-cou-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
  align-items: flex-start;
}
@media (max-width: 1000px) { .xl-cou-grid { grid-template-columns: 1fr } }
`;

const emptyEditing = { id: null };

export default function CouriersPageXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);

  const cx = useCouriersXL();
  const [editing, setEditing] = useState(emptyEditing);

  const onEdit = (c) => {
    setEditing({
      id: c.id,
      nombre: c.nombre || '',
      url_base_tracking: c.url_base_tracking || '',
      pais: c.pais || '',
      logo_url: c.logo_url || '',
      is_active: !!c.is_active,
    });
  };

  const onDelete = async (c) => {
    if (!window.confirm('Desactivar courier "' + c.nombre + '"?')) return;
    try {
      await cx.remove(c.id);
      cx.pushMsg('ok', 'Courier desactivado');
    } catch (e) {
      cx.pushMsg('err', (e && e.message) || 'Error al desactivar');
    }
  };

  const onSubmit = async (form) => {
    await cx.save(form);
    cx.pushMsg('ok', form.id ? 'Courier actualizado' : 'Courier creado');
    setEditing(emptyEditing);
  };

  const showWelcome = cx.items.length === 0 && !cx.loading;

  return (
    <div className="xl-page">
      <header className="xl-page__head">
        <div>
          <h2 className="xl-page__title">Couriers</h2>
          <p className="xl-page__subtitle">
            Catalogo de transportadoras. Usadas en importaciones tipo courier y maritima.
          </p>
        </div>
        <button type="button"
          className="xl-cou-presets"
          onClick={cx.cargarPresets}
          disabled={cx.loadingPresets}
          title="Agrega DHL, FedEx, UPS, Servientrega y mas con sus URLs de rastreo configuradas">
          <IconSparkles />
          {cx.loadingPresets ? 'Cargando\u2026' : 'Cargar couriers comunes'}
        </button>
      </header>

      {showWelcome && (
        <div className="xl-cou-welcome" role="note">
          <span className="xl-cou-welcome__ico"><IconSparkles /></span>
          <div>
            <div className="xl-cou-welcome__title">Primera vez aca?</div>
            <div className="xl-cou-welcome__body">
              Pulsa <b>Cargar couriers comunes</b> y tendras DHL, FedEx, UPS,
              Servientrega y otros listos para usar.
            </div>
          </div>
        </div>
      )}

      {cx.msg && (
        <div
          className={'xl-banner ' + (cx.msg.kind === 'ok' ? 'xl-banner--ok' : 'xl-banner--err')}
          role={cx.msg.kind === 'ok' ? 'status' : 'alert'}
          onClick={cx.clearMsg}>
          {cx.msg.text}
        </div>
      )}
      {cx.err && <div className="xl-banner xl-banner--err" role="alert">{cx.err}</div>}

      <div className="xl-cou-grid">
        <CouriersListXL
          items={cx.items}
          loading={cx.loading}
          q={cx.q}
          setQ={cx.setQ}
          onEdit={onEdit}
          onDelete={onDelete}
        />
        <CourierFormXL
          key={editing.id || 'new'}
          initial={editing.id ? editing : undefined}
          onSubmit={onSubmit}
          onCancel={() => setEditing(emptyEditing)}
        />
      </div>
    </div>
  );
}
